import React, { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";

import AddProductToShoppingCartButton from "../../atoms/AddProductToShoppingCartButton";
import Button from "../../atoms/Button";
import { showSuccessAlert } from "../../../helpers/alerts";
import { markGuestBoxIntent, markGuestDirectAddsChosen } from "../../../helpers/guestCheckout";
import { I18N_NAMESPACES } from "../../../helpers/constants";
import urlPatterns from "../../../urls";
import { fonts } from "../../../styles/variables";

export interface GuestBoxAddItem {
  quantity?: number | null;
  product: {
    id: number;
    name: string;
    [key: string]: unknown;
  };
}

interface GuestBoxAddActionsProps {
  // A wine or any other product the guest wants in their first box.
  item: GuestBoxAddItem;
  onAdded: () => void;
  // Replaces the shared "Wine added to monthly box" toast (e.g. with a product-named one).
  successMessage?: ReactNode;
}

const MemberQuestion = styled.p`
  margin: 0 0 12px;
  font-family: ${fonts.fontBauMedium};
`;

const ActionSpacer = styled.div`
  height: 10px;
`;

/**
 * The box slot of the add-bottle tray for logged-out visitors. Instead of bouncing
 * straight to the login page (losing the item), ask whether they already have an
 * account: members log in with the item captured in the local cart (the post-login
 * sync carries it over), new visitors keep building their box locally and sign up
 * with their email at the guest checkout.
 */
const GuestBoxAddActions = ({ item, onAdded, successMessage }: GuestBoxAddActionsProps) => {
  const { t } = useTranslation([I18N_NAMESPACES.COMMON]);
  const history = useHistory();
  const location = useLocation();
  const [isAskingAboutMembership, setIsAskingAboutMembership] = useState(false);

  // Both paths add to the box, so the toast must read "monthly box" — or the caller's own copy.
  const toastProps = successMessage
    ? { showSuccessToast: false, onAddSuccess: () => showSuccessAlert(successMessage, {}) }
    : { successMessageIsBox: true };

  if (!isAskingAboutMembership) {
    return (
      <Button type="button" onClick={() => setIsAskingAboutMembership(true)} full lg whiteBgOnHover>
        {t("common:addToFirstBox")}
      </Button>
    );
  }

  return (
    <div data-testid="guest-box-add-actions">
      <MemberQuestion>Already have an account?</MemberQuestion>
      <AddProductToShoppingCartButton
        product={item.product}
        quantity={item.quantity}
        label="Yes — log in and add it"
        showShoppingCart={false}
        {...toastProps}
        callbackAfterAdd={() => {
          // Box intent rides along so the post-login sync lands the item in their
          // first box (ignored server-side for anyone subscribed before).
          markGuestBoxIntent();
          onAdded();
          history.push(urlPatterns.LOGIN, { redirectUrl: location.pathname });
        }}
        full
        lg
        whiteBgOnHover
      />
      <ActionSpacer />
      <AddProductToShoppingCartButton
        product={item.product}
        quantity={item.quantity}
        label="Not yet — keep building my box"
        showShoppingCart={false}
        {...toastProps}
        callbackAfterAdd={() => {
          // Their box builds in the local cart; stop re-asking on later adds and
          // let the guest checkout collect their email when they're ready. The box
          // intent makes checkout hydration create their first subscription box.
          markGuestBoxIntent();
          markGuestDirectAddsChosen();
          onAdded();
        }}
        full
        lg
        ghost
      />
    </div>
  );
};

export default GuestBoxAddActions;
